// src/pages/ContactPage.jsx  —  ohapotato v2026.10.36
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function ContactPage() {
  useEffect(() => { document.title = 'Contact — OhAPotato 🥔'; window.scrollTo(0, 0); }, []);

  const [form,    setForm   ] = useState({ name: '', email: '', message: '' });
  const [status,  setStatus ] = useState('idle'); // idle | sending | sent | error

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(form),
      });
      // Safely parse — Netlify may return HTML on routing errors
      const text = await res.text();
      let data = {};
      try { data = JSON.parse(text); } catch { /* non-JSON response */ }
      if (res.ok && data.ok) {
        setStatus('sent');
        setForm({ name: '', email: '', message: '' });
      } else {
        throw new Error(data.detail || data.error || (res.ok ? 'Unexpected response' : `Server error ${res.status}`));
      }
    } catch (err) {
      console.error('[contact]', err.message);
      setStatus('error:' + err.message);
    }
  };

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <Link to="/" style={s.navHome}>← Back to OhAPotato</Link>
      </nav>

      <main style={s.main}>
        <header style={s.header}>
          <h1 style={s.title}>Get in touch</h1>
          <p style={s.sub}>
            Questions, feedback, bug reports, or just want to say hello — we read
            everything and reply to as much as we can.
          </p>
        </header>

        {status === 'sent' ? (
          <div style={s.successBox}>
            <span style={s.successIcon}>✓</span>
            <div>
              <p style={s.successTitle}>Message sent!</p>
              <p style={s.successBody}>Thanks for reaching out. We'll get back to you shortly.</p>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            style={s.form}
          >
            <div style={s.field}>
              <label style={s.label} htmlFor="name">Name</label>
              <input
                id="name" name="name" type="text"
                required placeholder="Your name"
                value={form.name} onChange={handleChange}
                style={s.input}
              />
            </div>

            <div style={s.field}>
              <label style={s.label} htmlFor="email">Email</label>
              <input
                id="email" name="email" type="email"
                required placeholder="you@example.com"
                value={form.email} onChange={handleChange}
                style={s.input}
              />
            </div>

            <div style={s.field}>
              <label style={s.label} htmlFor="message">Message</label>
              <textarea
                id="message" name="message"
                required rows={6}
                placeholder="What's on your mind?"
                value={form.message} onChange={handleChange}
                style={{ ...s.input, ...s.textarea }}
              />
            </div>

            {status.startsWith('error') && (
              <p style={s.errorText}>
                {status.includes(':') ? status.split(':').slice(1).join(':') : 'Something went wrong — please try again or email us directly.'}
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'sending'}
              style={{ ...s.submitBtn, ...(status === 'sending' ? s.submitBtnDisabled : {}) }}
            >
              {status === 'sending' ? 'Sending…' : 'Send message →'}
            </button>
          </form>
        )}

        <section style={s.altContact}>
          <h2 style={s.altTitle}>Other ways to reach us</h2>
          <p style={s.altBody}>
            You can also email us directly at{' '}
            <a href="mailto:hello@ohapotato.com" style={s.link}>hello@ohapotato.com</a>{' '}
            and we'll get back to you as soon as we can.
          </p>
        </section>

        <footer style={s.footer}>
          <Link to="/" style={s.footerLink}>Home</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/about" style={s.footerLink}>About</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/privacy" style={s.footerLink}>Privacy Policy</Link>
        </footer>
      </main>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)', transition: 'background 0.3s, color 0.3s' },
  nav:  { padding: '16px 32px', borderBottom: '1px solid var(--border-base)' },
  navHome: { fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--accent-orange)', textDecoration: 'none', fontWeight: '600' },
  main: { maxWidth: '560px', margin: '0 auto', padding: '48px 24px 80px' },

  header: { marginBottom: '36px' },
  title:  { fontFamily: 'var(--font-display)', fontSize: '32px', fontWeight: '400', marginBottom: '12px', color: 'var(--text-primary)' },
  sub:    { fontFamily: 'var(--font-body)', fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.65' },

  form:  { display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '40px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)' },
  input: {
    fontFamily: 'var(--font-body)', fontSize: '14px',
    padding: '10px 13px', borderRadius: 'var(--radius-md)',
    border: '1.5px solid var(--border-base)',
    background: 'var(--bg-card)', color: 'var(--text-primary)',
    outline: 'none', transition: 'border-color 0.2s',
    width: '100%', boxSizing: 'border-box',
  },
  textarea: { resize: 'vertical', lineHeight: '1.55' },

  submitBtn: {
    padding: '12px 24px', border: 'none', borderRadius: 'var(--radius-md)',
    background: 'var(--accent-orange)', color: '#fff',
    fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: '700',
    cursor: 'pointer', transition: 'opacity 0.2s', alignSelf: 'flex-start',
  },
  submitBtnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  errorText: { fontFamily: 'var(--font-body)', fontSize: '13px', color: '#EF4444' },

  successBox: {
    display: 'flex', gap: '16px', alignItems: 'flex-start',
    background: 'var(--accent-green-bg)', border: '1.5px solid var(--accent-green)',
    borderRadius: 'var(--radius-md)', padding: '20px', marginBottom: '40px',
  },
  successIcon:  { fontSize: '20px', color: 'var(--accent-green)', flexShrink: 0, fontWeight: '700' },
  successTitle: { fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: '700', color: 'var(--accent-green)', marginBottom: '4px' },
  successBody:  { fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--text-secondary)' },

  altContact: { paddingTop: '32px', borderTop: '1px solid var(--border-base)' },
  altTitle:   { fontFamily: 'var(--font-display)', fontSize: '16px', fontWeight: '400', marginBottom: '10px', color: 'var(--text-primary)' },
  altBody:    { fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.65' },
  link:       { color: 'var(--accent-orange)', textDecoration: 'none' },

  footer:     { marginTop: '64px', paddingTop: '24px', borderTop: '1px solid var(--border-base)', display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center' },
  footerLink: { fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--text-muted)', textDecoration: 'none' },
  footerDot:  { color: 'var(--text-muted)', fontSize: '11px' },
};
